import { useEffect, useState } from "react";
import { useOrders } from "../useHook";
import { useLocation } from "react-router-dom";
import { IoPrint, IoDownload } from "react-icons/io5";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";
import { getPaymentInfo, getCustomerReceiptUrl, formatPaymentMethodLabel } from "../orderPaymentInfo";

function pickupSnapshot(order) {
  return (
    order?.userBillingAddress?.pickupPoint ||
    order?.locationInfo?.pickupPoint ||
    null
  );
}

function isSelfPickupOrder(order) {
  const type = String(
    order?.deliveryType?.type ||
      order?.deliveryType?.deliveryType ||
      "",
  ).toLowerCase();
  return (
    order?.userBillingAddress?.selfPickup === true ||
    type === "self pickup" ||
    type === "self_pickup"
  );
}

function CardPaymentBanner({ order, canDownload }) {
  const info = getPaymentInfo(order);
  const receiptUrl = getCustomerReceiptUrl(order);
  if (info.methodKey !== "card" || !info.paymentCompleted) return null;

  return (
    <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4">
      <p className="text-sm font-bold text-green-800">
        Paid with Credit Debit Card Payment
      </p>
      <p className="text-sm text-green-700 mt-1">
        Webhook called successfully. Payment completed and processed.
      </p>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700">
        {info.cardBrand && (
          <p>
            <span className="font-semibold">Card:</span> {info.cardBrand}
            {info.cardNumber ? ` ${info.cardNumber}` : ""}
          </p>
        )}
        {info.authorizationCode && (
          <p>
            <span className="font-semibold">Auth code:</span>{" "}
            {info.authorizationCode}
          </p>
        )}
        {info.receipt && (
          <p>
            <span className="font-semibold">Gateway receipt:</span> {info.receipt}
          </p>
        )}
        {info.gatewayCode && (
          <p>
            <span className="font-semibold">Gateway:</span> {info.gatewayCode}
          </p>
        )}
      </div>
      {receiptUrl ? (
        <div className="mt-3 space-y-2 no-print">
          <img
            src={receiptUrl}
            alt="Customer payment screenshot"
            className="max-h-48 rounded border border-green-200 bg-white object-contain"
          />
          {canDownload && (
            <a
              href={receiptUrl}
              download={`payment-screenshot-${order.orderId}.png`}
              target="_blank"
              rel="noreferrer"
              className="bg-purple-500 text-white px-4 py-2 rounded shadow hover:bg-purple-600 inline-flex items-center gap-2"
            >
              <IoDownload />
              Download screenshot
            </a>
          )}
        </div>
      ) : (
        <div className="mt-3 no-print">
          <button
            type="button"
            disabled
            className="bg-gray-300 text-gray-500 px-4 py-2 rounded inline-flex items-center gap-2 cursor-not-allowed"
          >
            <IoDownload />
            Download
          </button>
          <p className="mt-1 text-xs italic text-gray-500">
            The customer has not uploaded yet
          </p>
        </div>
      )}
    </div>
  );
}

const Invoice = () => {
  const { hasPermission } = usePermission();
  const canPrint = hasPermission(SystemPermissions.Orders.PRINT);
  const canDownloadSlip = hasPermission(SystemPermissions.Orders.DOWNLOAD_SLIP);

  const location = useLocation();
  const { orderId, autoPrint } = location.state || {};
  const { getOrderByOrderId } = useOrders();
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    if (orderId) {
      getOrderByOrderId(orderId, setOrderData);
    }
  }, [orderId]);

  // Auto-print when orderData is loaded and autoPrint flag is true
  useEffect(() => {
    if (autoPrint && orderData && canPrint) {
      // Small delay to ensure the page is fully rendered
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoPrint, orderData, canPrint]);

  const products = orderData?.productInfo || [];

  // Use the new response structure fields
  // The API now provides: originalPrice, discountedPrice, discountPercentage, itemDiscountedTotal
  const totalAmount = orderData?.totalAmount
    ? parseFloat(orderData.totalAmount)
    : products.reduce(
      (sum, product) => sum + (parseFloat(product.itemDiscountedTotal) || 0),
      0
    );

  const createdAt = new Date(orderData?.createdAt);
  const formattedDate = createdAt
    .toLocaleDateString("en-GB")
    .replace(/\//g, "-");
  const formattedTime = createdAt.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const pickup = pickupSnapshot(orderData);
  const selfPickup = isSelfPickupOrder(orderData);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        .invoice-container {
          overflow-x: hidden;
        }
        .invoice-table-wrapper {
          overflow-x: auto;
        }
        @media print {
          @page {
            margin: 15mm;
            size: A4;
          }
          body * {
            visibility: hidden;
          }
          .invoice-container, .invoice-container * {
            visibility: visible;
          }
          .invoice-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
            background: white;
          }
          .invoice-container .bg-gray-50 {
            background: white !important;
          }
          .no-print {
            display: none !important;
          }
          /* Ensure table prints properly */
          table {
            page-break-inside: auto;
            width: 100%;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          /* Remove scrollbars in print */
          .overflow-x-auto {
            overflow: visible !important;
          }
          /* Ensure table fits on page */
          .invoice-container {
            overflow: visible !important;
          }
        }
      `}</style>
      <div className="invoice-container bg-gray-50 p-10 print:p-0 print:bg-white">
        <div className="bg-white p-8 shadow-md rounded-lg mx-auto print:shadow-none print:rounded-none">
          <div className="flex justify-between items-center border-b pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-green-700">INVOICE</h1>
              <p className="text-gray-500">{orderData?.orderId}</p>
            </div>
            {canPrint && (
              <button
                className="btn btn-action print:hidden no-print"
                onClick={handlePrint}
                title="Print Invoice"
              >
                <IoPrint className="text-white" /> Print
              </button>
            )}
          </div>
          <div>
            <h1 className="text-4xl font-bold text-green-700">GharPlans</h1>
          </div>
          <div className="flex justify-between mb-6">
            <div>
              <h2 className="font-semibold text-lg">Bill From:</h2>
              <p className="text-gray-700">Address: 469 G1 Johar Town Lahore</p>
              <p className="text-gray-700">Phone: +92 339 5111199</p>
              <p className="text-gray-500">Email: info@gharplans.com</p>
            </div>
            <div>
              <h2 className="font-semibold text-lg">Bill To:</h2>
              <p className="text-gray-700">
                Name: {orderData?.user?.firstName} {orderData?.user?.lastName}
              </p>
              <p className="text-gray-500">Email: {orderData?.user?.email}</p>
              {orderData?.userBillingAddress && (
                <>
                  {selfPickup && pickup?.address ? (
                    <p className="text-gray-700 mt-2">
                      Pickup: {pickup.name ? `${pickup.name} · ` : ""}
                      {pickup.address}
                    </p>
                  ) : (
                    <p className="text-gray-700 mt-2">
                      Address: {orderData.userBillingAddress.streetAddress}
                    </p>
                  )}
                  <p className="text-gray-700">
                    {orderData.userBillingAddress.city}
                    {orderData.userBillingAddress.postalCode
                      ? `, ${orderData.userBillingAddress.postalCode}`
                      : ""}
                  </p>
                  {orderData.userBillingAddress.contactNo && (
                    <p className="text-gray-700">Phone: {orderData.userBillingAddress.contactNo}</p>
                  )}
                </>
              )}
            </div>
            <div>
              <h2 className="font-semibold text-lg">Date:</h2>
              <p className="text-gray-500">{formattedDate}</p>
              <p className="text-gray-500">{formattedTime}</p>
              <div className="mt-3">
                <p className="text-gray-700">
                  <span className="font-semibold">Payment Method:</span>{" "}
                  {formatPaymentMethodLabel(orderData?.paymentType)}
                </p>
                <p className="text-gray-700">
                  <span className="font-semibold">Delivery Mode:</span>{" "}
                  {orderData?.deliveryType?.type ||
                    orderData?.deliveryType?.deliveryType ||
                    "Self Pickup"}
                </p>
                {orderData?.totalWeight && (
                  <p className="text-gray-700">
                    <span className="font-semibold">Weight:</span> {orderData.totalWeight} {orderData?.unit || "kg"}
                  </p>
                )}
              </div>
            </div>
          </div>

          <CardPaymentBanner order={orderData} canDownload={canDownloadSlip} />

          <div className="invoice-table-wrapper overflow-x-auto print:overflow-visible">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-4 py-2 text-left w-16">Sr. No</th>
                  <th className="border px-4 py-2 text-left w-2/5">Product Name</th>
                  <th className="border px-4 py-2 text-left w-28">Variant</th>
                  <th className="border px-4 py-2 text-left w-24">Color</th>
                  <th className="border px-4 py-2 text-left w-20">Quantity</th>
                  <th className="border px-4 py-2 text-left w-24">Price</th>
                  <th className="border px-4 py-2 text-left w-24">Discount</th>
                  <th className="border px-4 py-2 text-left w-24">Rate</th>
                  <th className="border px-4 py-2 text-left w-24">Total</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => {
                  // Use new response structure fields
                  const originalPrice = parseFloat(product.originalPrice) || 0;
                  const discountedPrice = parseFloat(product.discountedPrice) || originalPrice;
                  const discountPercentage = product.discountPercentage || 0;
                  const itemTotal = parseFloat(product.itemDiscountedTotal) || 0;
                  const variantData = orderData?.variantProductData?.[index];
                  const variantName = variantData?.variant?.name ?? "—";
                  const variantColor = variantData?.variantColor?.color ?? "—";

                  return (
                    <tr key={index}>
                      <td className="border px-4 py-2 w-16">{index + 1}</td>
                      <td className="border px-4 py-2 break-words">{product.productName}</td>
                      <td className="border px-4 py-2 break-words">{variantName}</td>
                      <td className="border px-4 py-2 break-words">{variantColor}</td>
                      <td className="border px-4 py-2 w-20 text-center">{product.quantity}</td>
                      <td className="border px-4 py-2 w-24 text-right">
                        {originalPrice.toFixed(2)}
                      </td>
                      <td className="border px-4 py-2 w-24 text-center">
                        {discountPercentage > 0 ? `${discountPercentage}%` : "0%"}
                      </td>
                      <td className="border px-4 py-2 w-24 text-right">
                        {discountedPrice.toFixed(2)}
                      </td>
                      <td className="border px-4 py-2 w-24 text-right">
                        {itemTotal.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-6">
            <div className="w-full md:w-1/3">
              <div className="flex justify-between mb-2">
                <p className="text-gray-700">Sub Total:</p>
                <p className="text-gray-700 font-semibold">
                  Rs. {totalAmount.toFixed(2)}
                </p>
              </div>
              {orderData?.deliveryCharges && parseFloat(orderData.deliveryCharges) > 0 && (
                <div className="flex justify-between mb-2">
                  <p className="text-gray-700">Shipping Charges:</p>
                  <p className="text-gray-700 font-semibold">
                    Rs. {parseFloat(orderData.deliveryCharges).toFixed(2)}
                  </p>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <p className="text-lg font-semibold text-green-700">Grand Total:</p>
                  <p className="text-2xl font-bold text-green-700">
                    Rs. {(totalAmount + (parseFloat(orderData?.deliveryCharges) || 0)).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoice;